'use client'

import { Button } from '@usitsdasdesign/dds-react';
import { getCreateArg, getUpdateArg } from 'helper/firmInitiateHelperService';
import React, { useEffect, useMemo, useState } from 'react'
import * as FilterService from 'services/filter.service';
import { createFirmInitiative, downloadBulkUploadTemplate, getFirmInitiativesExport, processFirmInitiatives, updateFirmInitiative, updateFirmInitiativeStatus } from 'services/firmInitiatives.service';
import FirmInitiativeTemplate from 'templates/FirmInitiativeTemplate';
import useGlobalToastStore from 'store/globalToastStore';
import { getFiltersString, getSearchString, getSortString, throttle } from 'lib/util';
import { trackAdobeAnalyticEvent } from 'services/analytics-tool.service';
import { getPollingStatus, recursiveCallUntillResultAvailable } from 'services/polling.service';
import { createDelegate } from 'services/delegate.service';
import { useSession } from 'next-auth/react';
import { getAllFirmInitiatives } from 'services/firmInitiatives.service';
import { getStructuredFirmInitativeList } from 'helper/firmInitiateHelperService';
import Loader from 'atoms/Loader';
import * as XLSX from "xlsx";
import { uploadFile } from 'helper/uploadFile';

const firmInitiativeHeaders = [
    "Initiative Title", 
    "Open Firmwide",
    "Function (L1)",
    "Service Area (L2)",
    "Sub-Service Area (L3)",
    "Sub-Sub Function (L4)",
    "Number of Openings", "Start Date",
    "End Date",
    "Approx. Hours/Day",
    "Initiative/Project Owner",
    "DL Email",
    "Location", "Grade Code", "Skill Set",
    "Entity",
    "Description"
]

export const getPermsissionToAddFirmInitiative = (user) => {
    const hasRole = user?.role?.some(rol => ["admin", 'guest_rm', 'firm_initiative'].includes(rol));
    const isDelegate = user?.delegateTypes?.some(del => ['firmInitiative'].includes(del));
    return hasRole || isDelegate;
}

 const fetchAllFirmInitiatives = async ({ offset = 0, limit = 10, search = null, filters = null, sorts = null }) => {
   const response = await getAllFirmInitiatives({ offset, limit, search, filters, sorts });
   return {
     rows: getStructuredFirmInitativeList({ firmInitiatives: response.entity?.[1]?.[0] }),
     count: response.entity?.[0]?.[0]?.[0]?.count
   };
 };

const limit = 10;

const FirmInitiativeClient = () => {
    const { data: { user } } = useSession();
    const { setToastData } = useGlobalToastStore();
    const [showLoading, setShowLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [paginationOption, setPaginationOption] = useState({
        currentPage: 1,
        pageNumberInSection: limit,
        pageLength: Math.ceil(total / limit)
    });
    const [filterAndSort, setFilterAndSort] = useState({});
    const [search, setSearch] = useState('');
    const [showDialog, setShowDialog] = useState('')
    const [showFirmInitiativesDrawer, setShowFirmInitiativesDrawer] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [defaultValues, setDefaultValues] = useState({});
    const [openBulkUploadDrawer, setOpenBulkUploadDrawer] = useState(false);
    const [filesState, setFileState] = useState([]);
    const [isDisabled, setDisabled] = useState(false);

    const fetchFinalFirmInitiates = async ({ offset = 0, limit = 10, search = null, filters = null, pageNo = 1, sorts= null }) => {
        setShowLoading(true);
        const data = await fetchAllFirmInitiatives({ offset, search, filters, sorts });
        setRows(data.rows);
        setTotal(data.count);
        setPaginationOption({
            currentPage: pageNo,
            pageNumberInSection: limit,
            pageLength: Math.ceil(data.count / limit)
        });
        setShowLoading(false);
    }
    const isbulkUploadEnabled = user?.role.includes('admin') || user?.role.includes('firm_initiative');
    const onSearch = async (search, filters, sort) => {
        const fil = getFiltersString(filters);
        const sorts = getSortString(sort)
        setFilterAndSort({ ...filterAndSort, filters: fil, sorts });
        fetchFinalFirmInitiates({ search, filters: fil, sorts })
    };

    const onFilterChange = (data) => {
        const search = getSearchString(data.filterSearchFor, data.filterSearch);
        setSearch(() => search);
        onSearch(search, data.filters, data?.sort);
        setFilterAndSort({ ...filterAndSort, filters: data.filters, sorts: data?.sort });
    };

    const loadOptions = (item, searchValue, data, page) => FilterService.getFiltersOptionData(item.name.replace('filters.', ''), searchValue, null, null, data, page);
    
    const downloadCsv = async () => {
        trackAdobeAnalyticEvent({
            reportType: "firm initiative export",
            filterApplied: Boolean(filterAndSort?.filters || search)
        })
        setShowLoading(true);
        function handleBeforeUnload(event) {
            event.preventDefault();
            event.returnValue = true;
        }

        window.addEventListener('beforeunload', handleBeforeUnload);
        const response = await getFirmInitiativesExport({
            search: search,
            filters: getFiltersString(filterAndSort?.filters),
            sorts: getSortString(filterAndSort?.sorts)
        });

        if (response?.status) {
            const { jobId } = response?.entity;
            recursiveCallUntillResultAvailable({
                callInMs: 1000,
                resultAvailable: false,
                onSuccess: ({ result }) => {
                    window.removeEventListener('beforeunload', handleBeforeUnload);
                    window.open(result.downloadLink, '_blank');
                    setShowLoading(false);
                },
                pollingStatusCb: async () => {
                    const result = await getPollingStatus({
                        messageUniqueId: jobId,
                        type: 'firm-initiative-export'
                    });

                    return result;
                },
                onError: async () => {
                    setToastData({ show: true, message: "Failed to Export", type: 'error' })
                    window.removeEventListener('beforeunload', handleBeforeUnload);
                }
            })
        } else {
            setToastData({ show: true, message: response?.error?.response?.data?.message ?? "Failed to Export", variant: 'error' })
            window.removeEventListener('beforeunload', handleBeforeUnload);
            setShowLoading(false);
        }
    };

    const throttleExportOnClick = throttle(downloadCsv);

    const onAddFirmInitiatives = async (data) => {
        const body = getCreateArg({ data })
        const response = await createFirmInitiative({ data: body })
        if (response.status) {
            const data = await fetchFinalFirmInitiates({});
            setToastData({
                show: true,
                message: `Firm Initiative- ${body.title} created successfully`,
                type: 'success'
            }),
                setShowFirmInitiativesDrawer(false);
        } else {
            setToastData({
                show: true,
                message: 'Failed to create Firm Initiative, please try again',
                type: 'error'
            });
        }
    }

    const onEditFirmInitiatives = async (data) => {
        const updateBody = getUpdateArg({data})
        const response = await updateFirmInitiative({ id: defaultValues.id, detail: updateBody })
        if (response.status) {
            const data = await fetchFinalFirmInitiates({});
            setToastData({
                show: true,
                message: 'Firm Initiative updated successfully',
                type: 'success'
            }),
                setShowFirmInitiativesDrawer(false);
        } else {
            setToastData({
                show: true,
                message: 'Failed to update Firm initiative!',
                type: 'error'
            });
        } 
    }

    const onCloseInitiate = async () => {
        const detail = {
            status: 'closed'
        }
        const response = await updateFirmInitiativeStatus({id: defaultValues?.id, detail})
        if (response.status) {
            const data = await fetchFinalFirmInitiates({});
            setToastData({
                show: true,
                message: 'Firm Initiative updated successfully',
                type: 'success'
            }),
            setShowDialog(null);
        } else {
            setToastData({
                show: true,
                message: 'Failed to update Firm initiative!',
                type: 'error'
            });
        }
    }

    const onDelegateSubmit = async (data) => {
        const delegateId = data.resource.id;
        const type = 'specificFirmInitiative';

        const delegateDetails = {
            type,
            delegateId,
            entityId: defaultValues?.id
        };

        const res = await createDelegate(delegateDetails);

        if (res.status) {
            setShowDialog('');
            fetchFinalFirmInitiates({})
            setToastData({ show: true, message: 'Firm initiative delegated successfully ' });
        } else {
            setToastData({ show: true, message: 'Failed to delegate Firm initiative', type: 'error' });
        }
    }

    const receivedFiles = (files) => {
        setFileState(files);
    };

    const deleteFile = () => {
        setFileState([]);
    };


    const downloadTemplate = async () => {

        const response = await downloadBulkUploadTemplate();
        if (response.status) {
            window.open(`api/resume?url=${response?.entity}`, '_blank');
        } else {
            setToastData({
                show: true,
                message: 'Failed to download template. Please try again.',
                type: 'error'
            });
        }
    };


    const uploadFirmInitiativeBulkFile= async () => {
        const uploadType = 'firm-initiative'
        setDisabled(true);
        setShowLoading(true);
        try {
            const file = filesState?.[0];
            if (file) {
                try {
                    const data = await file.arrayBuffer();
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames.find(name => name === 'Firm Initiatives');
                    if (!sheetName) {
                        setDisabled(false);
                        setToastData({
                            show: true,
                            message: "Uploaded file doesn’t match the required format. Please use the correct bulk upload template.",
                            type: 'error'
                        });
                        return;
                    }
                    const sheet = workbook.Sheets[sheetName];
                    const sheetHeaders = XLSX.utils.sheet_to_json(sheet, { header: 1, range: "A1:Q1" })[0] || [];

                    const headersMatch = firmInitiativeHeaders.every(header => sheetHeaders.includes(header));
                    if (!headersMatch) {
                        const missingHeaders = firmInitiativeHeaders.filter(header => !sheetHeaders.includes(header));
                        console.log('Missing headers:', missingHeaders);
                    }
                    if (!headersMatch) {
                        setToastData({
                            show: true,
                            message: "Uploaded file doesn’t match the required format. Please use the correct bulk upload template.",
                            type: 'error'
                        });
                        return;
                    }

                    const path = 'firm-initiative/'

                    try {
                        const filePath = await uploadFile(file, path);
                        setToastData({
                            show: true,
                            message: "File uploaded successfully. You will receive an email after it's processed.",
                            type: 'success'
                        });

                        const response = await processFirmInitiatives({
                            data: {
                                userId: '',
                                codeName: path + Date.now(),
                                filePath: filePath?.key,
                                type: uploadType
                            }
                        });

                        if (response.status) {
                            console.log('Uploading file...', filesState);
                            setOpenBulkUploadDrawer(false);
                            setFileState([]);
                        } else {
                            setToastData({
                                show: true,
                                message: "Something went wrong",
                                type: 'error'
                            });
                        }
                    } catch (error) {
                        setToastData({
                            show: true,
                            message: "Failed to upload file",
                            type: 'error'
                        });
                    }

                } catch (error) {
                    setToastData({
                        show: true,
                        message: "Failed to parse the file. Please check the format.",
                        type: 'error'
                    });
                }
            }
        } catch (error) {
            setToastData({
                show: true,
                message: "Failed to upload the file. Please try again.",
                type: 'error'
            });
        } finally {
            setShowLoading(false);
            setDisabled(false);
        }
    }

    const firmInitiativeRmAdminDataModel = useMemo(() => ({
        filter: {
            title: 'Firm Initiatives',
            view: 'FirmInitiative',
            onFilterChange,
            defaultFilter: {},
            FilterService: {},
            session: {},
            sortItems: [
                {
                    id: 1,
                    isActive: false,
                    type: 'sorting',
                    label: 'Initiative Title',
                    name: 'sort.initiativeTitle',
                },
                {
                    id: 2,
                    isActive: false,
                    type: 'sorting',
                    label: 'Status',
                    name: 'sort.status'
                },
                {
                    id: 3,
                    isActive: false,
                    type: 'sorting',
                    label: 'Number of openings',
                    name: 'sort.numberOfOpenings'
                },
                {
                    id: 4,
                    isActive: false,
                    type: 'sorting',
                    label: 'Employees Interested',
                    name: 'sort.employeesInterested'
                },
                {
                    id: 5,
                    isActive: false,
                    type: 'sorting',
                    label: 'Start date',
                    name: 'sort.startDate'
                },
                {
                    id: 6,
                    isActive: false,
                    type: 'sorting',
                    label: 'End date',
                    name: 'sort.endDate'
                },
                {
                    id: 7,
                    isActive: false,
                    type: 'sorting',
                    label: 'Approx. Hours/days',
                    name: 'sort.approxHoursPerDays'
                },
                {
                    id: 8,
                    isActive: false,
                    type: 'sorting',
                    label: 'Created At',
                    name: 'sort.createdAt'
                },
                {
                    id: 9,
                    isActive: false,
                    type: 'sorting',
                    label: 'Updated At',
                    name: 'sort.updatedAt'
                }
            ],
            filterItems: [
                {
                    id: 1,
                    label: 'Function',
                    isActive: false,
                    type: 'select',
                    name: 'filters.function',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 2,
                    label: 'Service area',
                    isActive: false,
                    type: 'select',
                    name: 'filters.serviceArea',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 3,
                    label: 'Sub-service area',
                    isActive: false,
                    type: 'select',
                    name: 'filters.subServiceArea',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 3.12,
                    label: 'Sub-Sub Function',
                    isActive: false,
                    type: 'select',
                    name: 'filters.subSubFunction',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 4,
                    label: 'Start date',
                    isActive: false,
                    type: 'calendar',
                    name: 'filters.startDateV2'
                },
                {
                    id: 5,
                    label: 'End date',
                    isActive: false,
                    type: 'calendar',
                    name: 'filters.endDateV2'
                },
                {
                    id: 6,
                    label: 'Location',
                    isActive: false,
                    type: 'select',
                    name: 'filters.location',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 7,
                    label: 'Grade',
                    isActive: false,
                    type: 'select',
                    name: 'filters.grade',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 8,
                    label: 'Skill Set',
                    isActive: false,
                    type: 'select',
                    name: 'filters.skillSetv1',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 9,
                    label: 'Entity',
                    isActive: false,
                    type: 'select',
                    name: 'filters.entity',
                    options: true,
                    isPaginate: true
                },
                {
                    id: 10,
                    label: 'Status',
                    isActive: false,
                    type: 'select',
                    name: 'filters.status',
                    options: [{
                        label: 'Open',
                        value: 'open'
                    },
                    {
                        label: 'Closed',
                        value: 'closed'
                    }],
                }
            ].map((item) => ({ ...item, loadOptions: (input, data, page) => loadOptions(item, input, data, page) })),
            searchFilterItems: [
                {
                    label: 'All',
                    value: 'all'
                },
                {
                    label: 'Initiative Title',
                    value: 'initiativeTitle'
                },
                {
                    label: 'Initiative Owner Name',
                    value: 'initiativeOwnerEmployeeName'
                },
                {
                    label: 'Initiative Emp Owner Code',
                    value: 'initiativeOwnerEmployeeCode'
                },
                {
                    label: 'Initiative Owner Email',
                    value: 'initiativeOwnerEmail'
                },
                {
                    label: 'DL Email',
                    value: 'dlEmail'
                },
                {
                    label: 'Created By',
                    value: 'createdBy'
                },
                {
                    label: 'Created Email',
                    value: 'createdEmail'
                },
                {
                    label: 'Created Emp. Code',
                    value: 'createdEmpCode'
                },
                {
                    label: 'Updated By',
                    value: 'updatedBy'
                },
                {
                    label: 'Updated Email',
                    value: 'updatedEmail'
                },
                {
                    label: 'Updated Emp. Code',
                    value: 'updatedEmpCode'
                },
            ],
            isFav: false,
            isExport: true,
            isBulkUpload: isbulkUploadEnabled ? true : false,
            onBulkUpload: () => setOpenBulkUploadDrawer(true),
            onExport: total > 0 ? throttleExportOnClick : () => setToastData({
                show: true,
                message: 'There is no data to be exported. Please try removing some filters if applied.',
                type: 'error'
            }),
            moreActionRight:
                <Button
                    size="l"
                    kind="primaryLoud"
                    label='Add Firm Initiative'
                    isDisabled={!getPermsissionToAddFirmInitiative(user)}
                    onClick={() => { setShowFirmInitiativesDrawer(true), setIsEdit(false); }}
                />
        },
        hideBulkUploadDrawer: () => setOpenBulkUploadDrawer(false),
        openBulkUploadDrawer,
        downloadTemplate,
        uploadFirmInitiativeBulkFile,
        receivedFiles,
        deleteFile,
        filesState,
        isDisabled,
        breadcrumbs: [
            {
                path: '/',
                title: 'Home'
            },
            {
                path: '/firm-initiatives-employee',
                title: 'Firm Initiatives'
            }
        ],
        table: {
            rows: rows,
            pagination: {
                currentPage: paginationOption.currentPage,
                pageLength: paginationOption.pageLength,
            },
            onPageChangeTable: async (pageNo) => {
                if (pageNo - 1 < total) {
                    await fetchFinalFirmInitiates({ offset: (pageNo - 1) * limit, pageNo, filters: getFiltersString(filterAndSort.filters), sorts: getSortString(filterAndSort?.sorts), search })
                }
            }
        },
        showFirmInitiativesDrawer,
        onClosFirmInitiativesDrawer: () => setShowFirmInitiativesDrawer(false),
        onAddFirmInitiatives,
        isEdit: isEdit,
        handleUpdate: (type, row) => {
            if (type === 'edit') {
                setDefaultValues(row.defaultValue)
                setShowFirmInitiativesDrawer(true)
                setIsEdit(true);
            }
        },
        handleClose: (type, row) => {
            if(type === 'close'){
                setDefaultValues(row.defaultValue)
                setShowDialog('close-initiative')
            }
        },
        handleDelegate: (type, row) => {
            if(type === 'delegate'){
                setDefaultValues(row.defaultValue)
                setShowDialog('add-delegates-drawer')
            }
        },
        defaultValues,
        onEditFirmInitiatives,
    }), [onAddFirmInitiatives, defaultValues, rows]);

    useEffect(() => {
        fetchFinalFirmInitiates({})
    }, [])

    return (
        <>
        <FirmInitiativeTemplate
            firmInitiativeRmAdminDataModel={firmInitiativeRmAdminDataModel}
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            onCloseInitiate={onCloseInitiate}
            onDelegateSubmit={onDelegateSubmit}
            defaultValues={defaultValues}
            showLoading={showLoading}
        />
        {showLoading && <Loader showInprogressOnly={true} />}
        </>
    )
}

export default FirmInitiativeClient